import React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

/** Option shape from `/products/externalunlinked` (integration product id + display fields) */
export interface UnlinkedProductOption {
    externalProductId: number;
    name: string;
    provider: string;
}

/** List panel at least as wide as the longest option; cap width on small viewports */
const autocompleteSlotProps = {
    popper: {
        sx: {
            width: "max-content",
            maxWidth: "min(90vw, 56rem)",
        },
    },
    paper: {
        sx: {
            width: "max-content",
            maxWidth: "min(90vw, 56rem)",
            boxSizing: "border-box" as const,
        },
    },
    listbox: {
        sx: {
            maxHeight: 280,
            "& .MuiAutocomplete-option": {
                whiteSpace: "nowrap" as const,
            },
        },
    },
};

export interface UnlinkedProductDropdownProps {
    options: UnlinkedProductOption[];
    value: number | null;
    onChange: (externalProductId: number | null) => void;
    /** When true and options are still empty, shows a loading row instead of the field */
    loading?: boolean;
    /** e.g. lazy-load when the user opens the list (create-product flow) */
    onOpen?: () => void;
    sectionTitle?: string;
    fieldLabel?: string;
    placeholder?: string;
    description?: string;
}

const UnlinkedProductDropdown: React.FC<UnlinkedProductDropdownProps> = ({
    options,
    value,
    onChange,
    loading = false,
    onOpen,
    sectionTitle = "Integration Product",
    fieldLabel = "Link to integration",
    placeholder = "Search products…",
    description,
}) => {
    if (loading && options.length === 0) {
        return (
            <Box sx={{ display: "flex", alignItems: "center", height: "56px", padding: "16px" }}>
                <CircularProgress size={20} />
                <Typography variant="body2" sx={{ ml: 2 }}>
                    Loading unlinked products...
                </Typography>
            </Box>
        );
    }

    const selected =
        value == null
            ? null
            : options.find((p) => p.externalProductId === value) ?? null;

    return (
        <>
            <Typography variant="body2" sx={{ mt: 1, mb: 0.5 }}>
                {sectionTitle}
            </Typography>
            <Autocomplete<UnlinkedProductOption, false, false, false>
                options={options}
                loading={loading && options.length > 0}
                onOpen={onOpen}
                getOptionLabel={(option) => `${option.name} (${option.provider})`}
                isOptionEqualToValue={(a, b) => a.externalProductId === b.externalProductId}
                value={selected}
                onChange={(_event, newValue) =>
                    onChange(newValue?.externalProductId ?? null)
                }
                clearOnEscape
                renderInput={(params) => (
                    <TextField
                        {...params}
                        margin="dense"
                        label={fieldLabel}
                        placeholder={placeholder}
                        fullWidth
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
                slotProps={autocompleteSlotProps}
            />
            {description ? (
                <Typography variant="caption" color="text.secondary" display="block">
                    {description}
                </Typography>
            ) : null}
        </>
    );
};

export default UnlinkedProductDropdown;
